import Header from '../components/Header/Header'
import Modal from '../components/Modal/Modal'
import Loading from '../components/Loading/Loading'
import PopupMessage from '../components/PopupMessage/PopupMessage'
import editIcon from '../assets/images/edit-red.svg'

import { useEffect, useState, useContext, useRef } from 'react'
import { AuthContext } from '../contexts/AuthContext'
import { getRoleText } from '../helpers/roles'
import { formatDate } from '../helpers/formatters'
import { validateEmail } from '../helpers/validators'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getProfile, updateProfile, deleteAccount } from '../api/users'
import useAuth from '../hooks/useAuth'
import useMediaQuery from '../hooks/useMediaQuery'
import { useNavigate } from 'react-router-dom'
import Dialog from '../components/Dialog/Dialog'

const Profile = () => {
    useEffect(() => {
        document.title = 'Προφίλ - Πλατφόρμα Ηλ. Πληρωμών Δήμου Δυτικής Λέσβου'
    }, [])

    const { getUserRole, getEmailVerified, logout } = useAuth()
    const isMobile = useMediaQuery('(max-width:1024px)')
    const isLowerRes = useMediaQuery('(max-width:380px)')
    const currentRole = getUserRole()
    const [email, setEmail] = useState(null)
    const [editing, setEditing] = useState(false)
    const [popup, setPopup] = useState({ message: '', type: '', shown: false })
    const { token } = useContext(AuthContext)
    const options = {
        headers: {
            Authorization: `Bearer ${token.access_token}`,
        },
    }
    const dialogRef = useRef(null)
    const navigate = useNavigate()
    const queryClient = useQueryClient()

    const { isLoading, data } = useQuery({
        queryKey: ['profile', options],
        queryFn: () => getProfile(options),
    })
    const mutation = useMutation({
        mutationFn: updatedProfile =>
            updateProfile({
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(updatedProfile),
            }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['profile'],
            })

            setPopup(values => ({
                ...values,
                shown: true,
                message: <p> Επιτυχής αλλαγή email </p>,
                type: 'success',
            }))

            // Logout user to login again with new email
            setTimeout(logout, 5000)
        },
        onError: () => {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Κάτι πήγε στραβά! </p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'error',
            }))
        },
    })
    const deleteAccountMutation = useMutation({
        mutationFn: () =>
            deleteAccount({
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${token.access_token}`,
                    'Content-Type': 'application/json',
                },
            }),
        onSuccess: () => {
            logout()
            navigate('/', { replace: true })
        },
        onError: () => {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Κάτι πήγε στραβά! </p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'error',
            }))
        },
    })

    if (isLoading) {
        return <Loading />
    }

    const handleEmailUpdate = async () => {
        if (!validateEmail(email)) {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Λάθος μορφή email! </p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'error',
            }))
            return
        }

        setEditing(false)

        const sendEmailVerificationOptions = {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token.access_token}`,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                email: email,
            }),
        }

        const emailVerification = await fetch(
            `${process.env.REACT_APP_API_URL}/auth/send-mail-verification`,
            sendEmailVerificationOptions
        )

        const emailRes = await emailVerification.json()
        if (emailRes) {
            mutation.mutate({
                email: email,
                role: currentRole,
                taxid: data.profile.taxid,
                firstName: data.profile.firstName,
                lastName: data.profile.lastName,
                fatherName: data.profile.fatherName,
                motherName: data.profile.motherName,
                birthYear: data.profile.birthYear || '1900',
            })
        } else {
            setPopup(values => ({
                ...values,
                shown: true,
                message: (
                    <>
                        <p>Κάτι πήγε στραβά! </p>
                        <p>Παρακαλώ προσπαθήστε ξανά.</p>
                    </>
                ),
                type: 'error',
            }))
            return
        }
    }
    const emailVerifed = getEmailVerified()
    const profileContent = (
        <div className="flex flex-col justify-center items-center w-full md:w-auto max-w-xl md:max-w-none md:my-14 md:mx-8">
            {!emailVerifed ? (
                <div className="flex justify-center items-center mb-8 w-full max-w-4xl">
                    <Modal classes="flex flex-col md:flex-row items-center gap-2 w-full max-w-4xl">
                        <div className="border-b-4 md:border-b-0 md:border-r-4 text-xl font-semibold ">
                            <h2 className="p-4 md:p-12 self-center">
                                Επιβεβαίωση του email
                            </h2>
                        </div>

                        <p className="p-4 text-center break-all md:break-normal md:text-start">
                            Προκειμένου να έχετε πλήρη πρόσβαση στην πλατφόρμα,
                            παρακαλούμε όπως συμπληρώσετε την ηλεκτρονική σας
                            διεύθυνση παρακάτω και στην συνέχεια ακολουθήστε τον
                            σύνδεσμο που θα σας έχει σταλεί στο ηλεκτρονικό σας
                            ταχυδρομείο.
                        </p>
                    </Modal>
                </div>
            ) : null}

            <Modal classes="flex flex-col  gap-4 w-full max-w-4xl my-5">
                <div className="border-b w-full border-black">
                    <h2 className="text-2xl text-center my-4">
                        {currentRole === 'CITIZEN'
                            ? 'Στοιχεία Δημότη'
                            : 'Στοιχεία Επιχείρησης'}
                    </h2>
                </div>

                <div className="w-full flex flex-col items-center gap-3 my-8">
                    <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                        <div className="w-1/2 h-max flex-auto px-8 my-1">
                            <p className="font-medium">ΑΦΜ</p>
                        </div>
                        <div className="w-1/2 h-max flex-auto">
                            <p className="font-medium text-secondary align-middle">
                                {data.profile.taxid ?? '-'}
                            </p>
                        </div>
                    </div>
                    <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                        <div className="w-1/2 h-max flex-auto px-8 my-1">
                            <p className="font-medium">
                                {currentRole === 'CITIZEN'
                                    ? 'Επώνυμο'
                                    : 'Επωνυμία'}
                            </p>
                        </div>
                        <div className="w-1/2 h-max flex-auto">
                            <p className="font-medium text-secondary">
                                {data.profile.lastName ?? '-'}
                            </p>
                        </div>
                    </div>
                    {currentRole === 'CITIZEN' && (
                        <>
                            <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                                <div className="w-1/2 h-max flex-auto px-8 my-1">
                                    <p className="font-medium">Όνομα</p>
                                </div>
                                <div className="w-1/2 h-max flex-auto">
                                    <p className="font-medium text-secondary">
                                        {data.profile.firstName ?? '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                                <div className="w-1/2 h-max flex-auto px-8 my-1">
                                    <p className="font-medium">Πατρώνυμο</p>
                                </div>
                                <div className="w-1/2 h-max flex-auto">
                                    <p className="font-medium text-secondary">
                                        {data.profile.fatherName ?? '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                                <div className="w-1/2 h-max flex-auto px-8 my-1">
                                    <p className="font-medium">Μητρώνυμο</p>
                                </div>
                                <div className="w-1/2 h-max flex-auto">
                                    <p className="font-medium text-secondary">
                                        {data.profile.motherName ?? '-'}
                                    </p>
                                </div>
                            </div>
                            <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                                <div className="w-1/2 h-max flex-auto px-8 my-1">
                                    <p className="font-medium">Έτος γέννησης</p>
                                </div>
                                <div className="w-1/2 h-max flex-auto">
                                    <p className="font-medium text-secondary">
                                        {data.profile.birthYear ?? '-'}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                    <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray break-all">
                        <div className="w-1/2 h-max flex-auto px-8 my-1">
                            <p className="font-medium">Email</p>
                        </div>
                        <div
                            className={`w-1/2 h-max flex-auto flex ${
                                isLowerRes ? 'flex-col' : null
                            } justify-between mr-4`}
                        >
                            {editing ? (
                                <input
                                    className="border-gray-300 rounded-md border-2 bg-gray-100 w-full mr-10 p-2"
                                    type="email"
                                    name="email"
                                    defaultValue={data.email}
                                    onChange={e => setEmail(e.target.value)}
                                />
                            ) : (
                                <>
                                    <p
                                        className={`font-medium text-secondary ${
                                            isLowerRes ? 'text-center' : null
                                        }`}
                                    >
                                        {data.email ?? '-'}
                                    </p>

                                    <button
                                        className={
                                            isLowerRes
                                                ? 'self-center pt-2'
                                                : null
                                        }
                                        onClick={setEditing}
                                    >
                                        <img src={editIcon} alt="edit-button" />
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                        <div className="w-1/2 h-max flex-auto px-8 my-1">
                            <p className="font-medium">Ρόλος</p>
                        </div>
                        <div className="w-1/2 h-max flex-auto">
                            <p className="font-medium text-secondary">
                                {getRoleText(data.role)}
                            </p>
                        </div>
                    </div>
                    <div className="profile-item py-1 flex w-full max-w-2xl border-b border-gray">
                        <div className="w-1/2 h-max flex-auto px-8 my-1">
                            <p className="font-medium">Ημερομηνία Εγγραφής</p>
                        </div>
                        <div className="w-1/2 h-max flex-auto">
                            <p className="font-medium text-secondary">
                                {formatDate(data.createdAt)}
                            </p>
                        </div>
                    </div>
                </div>

                {editing && (
                    <div className="flex flex-col md:flex-row justify-end m-10">
                        <button
                            className="self-center m-3 w-48 md:order-2 bg-primary text-white border border-primary hover:bg-white hover:text-primary rounded-lg py-3.5 px-10 transition-all"
                            onClick={handleEmailUpdate}
                        >
                            Αποθήκευση
                        </button>
                        <button
                            className="md:mr-14 m-3 w-48 self-center bg-white text-primary border border-primary hover:bg-primary hover:text-white rounded-lg py-3.5 px-10 transition-all"
                            onClick={() => {
                                setEmail(data.email)
                                setEditing(!editing)
                            }}
                        >
                            Ακύρωση
                        </button>
                    </div>
                )}

                <button
                    onClick={() => {
                        dialogRef.current?.showModal()
                    }}
                    className="bg-[#DD655B] text-white w-fit self-center lg:self-end px-12 py-3 rounded-lg mb-14 md:mx-14 hover:text-[#DD655B] border hover:border-[#DD655B] hover:bg-white transition-colors"
                >
                    Διαγραφή Λογαριασμού
                </button>
            </Modal>
        </div>
    )

    return (
        <>
            {popup.shown && (
                <PopupMessage
                    message={popup.message}
                    type={popup.type}
                    closePopUp={() =>
                        setPopup(values => ({ ...values, shown: false }))
                    }
                    showCloseButton={true}
                />
            )}

            <Dialog ref={dialogRef}>
                <h1 className="text-xl text-center text-[#2C3C46] mb-12">
                    Θέλετε να διαγράψετε οριστικά το λογαριασμό σας;
                </h1>

                <div className="flex flex-wrap justify-center items-center gap-4">
                    <button
                        onClick={() => dialogRef.current?.close()}
                        className="text-[#0F3A4A] border-2 border-[#0F3A4A] px-6 py-3 font-medium text-base rounded-lg"
                    >
                        Ακύρωση
                    </button>

                    <button
                        onClick={() => deleteAccountMutation.mutate()}
                        disabled={deleteAccountMutation.isLoading}
                        className="bg-[#DD655B] text-white border-2 border-[#DD655B] px-6 py-3 font-medium text-base rounded-lg"
                    >
                        Οριστική Διαγραφή
                    </button>
                </div>
            </Dialog>

            {isMobile ? (
                profileContent
            ) : (
                <Modal classes="w-full max-w-7xl flex flex-col">
                    <Header
                        includeLogo={false}
                        withBorder={true}
                        includeButtons={true}
                    />

                    {profileContent}
                </Modal>
            )}
        </>
    )
}

export default Profile
